import { FC, useState } from 'react';
import clsx from 'clsx';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Grid, Button, Divider, Box, TextField, InputAdornment } from '@mui/material';
import { FilterList, Close, ArrowDropDown, ArrowDropUp, Search } from '@mui/icons-material';
import { ILot } from '../../../models';
import { NeighborhoodLotsDropdown } from '../../../components';
import { SalesNeighborhoodDropdown } from '../../../components/dropdowns/sales-neighborhood-dropdown';

interface IManageSpecFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  searchValue: string;
  searchedValue: string;
  setSearch: (val: string) => void;
  setSearchedValue: (val: string) => void;
  setPage: (val: number) => void;
  handleSearch: (clearSearch?: boolean) => void;
  lots: ILot[];
  selectedLotId: string;
  setSelectedNeighborhoodId: (val: any) => void;
  isLoadingNeighborhoodLots: boolean;
  setSelectedLotId: (val: any) => void;
  selectedNeighborhoodId: string;
  fetchSpecs: () => void;
}

export const ManageSpecFilters: FC<IManageSpecFilters> = ({
  isLoading,
  applyFilters,
  searchValue,
  searchedValue,
  setSearch,
  setSearchedValue,
  setPage,
  selectedNeighborhoodId,
  lots,
  selectedLotId,
  setSelectedNeighborhoodId,
  isLoadingNeighborhoodLots,
  setSelectedLotId
}) => {
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [isMobileFilterButtonOpen, toggleMobileFilter] = useState<boolean>(false);
  const classes = useStyles({ isMobileFilterButtonOpen });

  return (
    <>
      <Grid container spacing={1} alignItems="center" className={classes.searchWrapper}></Grid>

      <Button
        color="secondary"
        variant="contained"
        className={classes.mobileButton}
        startIcon={<FilterList />}
        endIcon={isMobileFilterButtonOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={() => {
          toggleMobileFilter(!isMobileFilterButtonOpen);
        }}
      >
        Filters
      </Button>
      {!isMobileFilterButtonOpen && <Divider className={classes.divider} />}
      <Grid container spacing={1} alignItems="center" className={classes.wrapper}>
        <Grid item xs={12} sm={6} md={4}>
          <Box display="flex">
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder="Search..."
              name="search"
              value={searchValue}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    className={classes.searchIcon}
                    onClick={() => {
                      if (searchValue.length > 0) {
                        setHasAppliedFilters(true);
                        setSearchedValue(searchValue);
                      }
                    }}
                  >
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment
                    position="end"
                    className={classes.searchIcon}
                    onClick={() => {
                      setHasAppliedFilters(false);
                      setSearch('');
                      setSearchedValue('');
                    }}
                  >
                    <Close />
                  </InputAdornment>
                ) : null
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && searchValue.length > 0) {
                  setHasAppliedFilters(true);
                  setSearchedValue(searchValue);
                }
              }}
              onChange={e => {
                setSearch(e.target.value);
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SalesNeighborhoodDropdown
            value={selectedNeighborhoodId}
            handleChange={value => {
              setSelectedNeighborhoodId(value);
            }}
            apiParams={{ forAgentNewBuilds: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <NeighborhoodLotsDropdown
            value={selectedLotId}
            isDisabled={!selectedNeighborhoodId}
            handleChange={value => setSelectedLotId(value)}
            apiParams={{ neighborhoodId: selectedNeighborhoodId }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            color="primary"
            variant="contained"
            className={classes.button}
            disabled={isLoading}
            startIcon={<FilterList />}
            onClick={() => {
              setSearchedValue(searchValue);
              setHasAppliedFilters(true);
              applyFilters();
            }}
          >
            Apply Filters
          </Button>
          {hasAppliedFilters && (
            <Button
              color="inherit"
              className={clsx(classes.button, classes.resetButton)}
              variant="contained"
              disabled={isLoading}
              startIcon={<Close />}
              onClick={() => {
                setHasAppliedFilters(false);
                setSelectedNeighborhoodId('');
                setSelectedLotId('');
                setSearch('');
                setSearchedValue('');
                applyFilters(true);
              }}
            >
              Reset
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles<Theme, { isMobileFilterButtonOpen?: boolean }>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  button: {
    marginTop: 4,
    marginBottom: 4,
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  },
  wrapper: ({ isMobileFilterButtonOpen }) =>
    isMobileFilterButtonOpen
      ? {
          marginTop: 10,
          marginBottom: 10,
          display: 'flex'
        }
      : {
          display: 'none',
          marginBottom: theme.spacing(1),
          [theme.breakpoints.up('sm')]: {
            display: 'flex',
            marginBottom: theme.spacing(1)
          }
        },
  divider: {
    display: 'block',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  gridItem: {
    padding: `3px 8px !important`,
    [theme.breakpoints.up('sm')]: {
      padding: `8px !important`
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  },
  ellipsis: {
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  },

  searchWrapper: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      marginBottom: theme.spacing(1)
    }
  },
  clearSearchButton: {
    '@media (min-width: 408px)': {
      marginTop: 3,
      marginLeft: theme.spacing(1)
    }
  },
  searchButton: {
    minWidth: '8rem'
  }
}));
